import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shuffle } from 'lucide-react';
import { motion } from 'framer-motion';

const JOKES = [
  // 31 jokes for free version
  { q: "Why don't scientists trust atoms?", a: "Because they make up everything!" },
  { q: "What do you call a bear with no teeth?", a: "A gummy bear!" },
  { q: "Why did the scarecrow win an award?", a: "Because he was outstanding in his field!" },
  { q: "What do you call fake spaghetti?", a: "An impasta!" },
  { q: "Why don't eggs tell jokes?", a: "They'd crack each other up!" },
  { q: "What's orange and sounds like a parrot?", a: "A carrot!" },
  { q: "Why did the bicycle fall over?", a: "Because it was two-tired!" },
  { q: "What do you call a fish with no eyes?", a: "Fsh!" },
  { q: "Why couldn't the leopard play hide and seek?", a: "Because he was always spotted!" },
  { q: "What did one wall say to the other?", a: "I'll meet you at the corner!" },
  { q: "What do you call a sleeping dinosaur?", a: "A dino-snore!" },
  { q: "Why do cows wear bells?", a: "Because their horns don't work!" },
  { q: "What do you call a dog that does magic?", a: "A Labracadabrador!" },
  { q: "Why don't oysters share?", a: "Because they're shellfish!" },
  { q: "What do you call a pig that knows karate?", a: "A pork chop!" },
  { q: "Why do seagulls fly over the sea?", a: "Because if they flew over the bay, they'd be bagels!" },
  { q: "What did the grape say when it got stepped on?", a: "Nothing, it just let out a little wine!" },
  { q: "Why did the cookie go to the doctor?", a: "Because it was feeling crummy!" },
  { q: "What do you call cheese that isn't yours?", a: "Nacho cheese!" },
  { q: "Why did the banana go to the doctor?", a: "It wasn't peeling well!" },
  { q: "What do you call a sad strawberry?", a: "A blueberry!" },
  { q: "Why did the tomato turn red?", a: "Because it saw the salad dressing!" },
  { q: "Why did the student eat his homework?", a: "Because his teacher said it was a piece of cake!" },
  { q: "Why did the math book look so sad?", a: "Because it had so many problems!" },
  { q: "What's a snake's favorite subject?", a: "Hissssstory!" },
  { q: "What did the ocean say to the beach?", a: "Nothing, it just waved!" },
  { q: "What do clouds wear under their shorts?", a: "Thunderpants!" },
  { q: "Why did the sun go to school?", a: "To get a little brighter!" },
  { q: "What do you call a boomerang that doesn't come back?", a: "A stick!" },
  { q: "Why can't your nose be 12 inches long?", a: "Because then it would be a foot!" },
  { q: "What has ears but cannot hear?", a: "A cornfield!" }
];

const GiggleGenerator = () => {
  const navigate = useNavigate();
  const [initialJoke] = useState(() => JOKES[Math.floor(Math.random() * JOKES.length)]);
  const [currentJoke, setCurrentJoke] = useState(initialJoke);
  const [showAnswer, setShowAnswer] = useState(false);
  const [seenJokes, setSeenJokes] = useState(() => new Set([initialJoke.q]));

  const getRandomJoke = () => {
    let newJoke = JOKES[Math.floor(Math.random() * JOKES.length)];
    while (newJoke.q === currentJoke.q && JOKES.length > 1) {
      newJoke = JOKES[Math.floor(Math.random() * JOKES.length)];
    }
    setCurrentJoke(newJoke);
    setShowAnswer(false);
    setSeenJokes(prev => {
      const next = new Set(prev);
      next.add(newJoke.q);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF9E3] to-[#FFE66D] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748]">Giggle Generator</h1>
          
          <button
            data-testid="new-joke-btn"
            onClick={getRandomJoke}
            className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
          >
            <Shuffle className="w-5 h-5" />
            New Joke
          </button>
        </header>

        <div className="text-center mb-4">
          <span className="bg-[#81B29A]/20 text-[#81B29A] px-4 py-1 rounded-full text-sm font-medium">
            Free Version - 31 Jokes
          </span>
        </div>

        <motion.div
          key={currentJoke.q}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-12 shadow-2xl border border-slate-100 text-center"
        >
          <div className="text-6xl mb-8">
            {showAnswer ? "🤣" : "😄"}
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-8 leading-relaxed">
            {currentJoke.q}
          </h2>
          
          {!showAnswer ? (
            <button
              data-testid="show-answer-btn"
              onClick={() => setShowAnswer(true)}
              className="bg-gradient-to-r from-[#FFE66D] to-[#FFCC00] text-[#2D3748] px-12 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg"
            >
              Show Answer
            </button>
          ) : (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <div className="text-xl md:text-2xl font-semibold text-[#81B29A] p-6 bg-[#E0F7FA] rounded-2xl mb-6">
                {currentJoke.a}
              </div>
              <button
                onClick={getRandomJoke}
                className="bg-[#81B29A] text-white px-8 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2 mx-auto"
              >
                <Shuffle className="w-5 h-5" />
                Another One!
              </button>
            </motion.div>
          )}
        </motion.div>

        <div className="mt-8 text-center">
          <p className="text-[#4A5568]">
            31 jokes to brighten your mood! You've seen {seenJokes.size} so far.
          </p>
          <p className="text-[#81B29A] font-medium mt-2">
            ⭐ Upgrade to Premium for 300 jokes!
          </p>
        </div>
      </div>
    </div>
  );
};

export default GiggleGenerator;
